/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { ComponentChildren, Fragment, h, VNode } from "preact";
import { useEffect, useState } from "preact/hooks";
import { AdminPaths } from "../../AdminRoutes.js";
import { InstancePaths } from "../../Routing.js";
import { NavigationBar } from "./NavigationBar.js";
import { Sidebar } from "./SideBar.js";
import { useSessionContext } from "../../context/session.js";
import {
  NotificationCardBulma,
  useNavigationContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { LangSelector } from "./LangSelector.js";
import { InternationalizationAPI } from "@gnu-taler/taler-util";
import { useSettingsContext } from "../../context/settings.js";

function getInstanceTitle(
  path: string,
  id: string,
  i18n: InternationalizationAPI,
): string {
  switch (path) {
    case InstancePaths.settings:
      return i18n.ctx("title")`${id}: Settings`;
    case InstancePaths.bank_new:
      return i18n.ctx("title")`${id}: New bank account`;
    case InstancePaths.bank_list:
      return i18n.ctx("title")`${id}: Bank accounts`;
    case InstancePaths.bank_update:
      return i18n.ctx("title")`${id}: Update bank Account`;
    case InstancePaths.order_list:
      return i18n.ctx("title")`${id}: Orders`;
    case InstancePaths.order_list_section:
      return i18n.ctx("title")`${id}: Orders`;
    case InstancePaths.order_new:
      return i18n.ctx("title")`${id}: New order`;
    case InstancePaths.inventory_list:
      return i18n.ctx("title")`${id}: Inventory`;
    case InstancePaths.inventory_new:
      return i18n.ctx("title")`${id}: New product`;
    case InstancePaths.inventory_update:
      return i18n.ctx("title")`${id}: Update product`;
    case InstancePaths.category_list:
      return i18n.ctx("title")`${id}: Category`;
    case InstancePaths.category_new:
      return i18n.ctx("title")`${id}: New category`;
    case InstancePaths.category_update:
      return i18n.ctx("title")`${id}: Update category`;
    case InstancePaths.transfers_list:
      return i18n.ctx("title")`${id}: Wire transfers`;
    case InstancePaths.webhooks_list:
      return i18n.ctx("title")`${id}: Webhooks`;
    case InstancePaths.webhooks_new:
      return i18n.ctx("title")`${id}: New webhook`;
    case InstancePaths.webhooks_update:
      return i18n.ctx("title")`${id}: Update webhook`;
    case InstancePaths.otp_devices_list:
      return i18n.ctx("title")`${id}: OTP devices`;
    case InstancePaths.otp_devices_new:
      return i18n.ctx("title")`${id}: New OTP device`;
    case InstancePaths.otp_devices_update:
      return i18n.ctx("title")`${id}: Update OTP device`;
    case InstancePaths.templates_new:
      return i18n.ctx("title")`${id}: New template`;
    case InstancePaths.templates_update:
      return i18n.ctx("title")`${id}: Update template`;
    case InstancePaths.templates_list:
      return i18n.ctx("title")`${id}: Templates`;
    case InstancePaths.templates_use:
      return i18n.ctx("title")`${id}: Use template`;
    case InstancePaths.interface:
      return i18n.ctx("title")`${id}: Personalization`;
    case InstancePaths.token_family_list:
      return i18n.ctx("title")`${id}: Discounts & Suscriptions`;
    case InstancePaths.token_family_new:
      return i18n.ctx("title")`${id}: New token family`;
    case InstancePaths.token_family_update:
      return i18n.ctx("title")`${id}: Update token family`;
    case InstancePaths.access_token_list:
      return i18n.ctx("title")`${id}: Access tokens`;
    case InstancePaths.access_token_new:
      return i18n.ctx("title")`${id}: New access token`;
    default: {
      return "";
    }
  }
}

function getAdminTitle(
  path: string,
  instance: string,
  i18n: InternationalizationAPI,
) {
  if (path === AdminPaths.new_instance) return i18n.ctx("title")`New instance`;
  if (path === AdminPaths.list_instances) return i18n.ctx("title")`Instances`;
  return getInstanceTitle(path, instance, i18n);
}

interface MenuProps {}

function WithTitle({
  title,
  children,
}: {
  title: string;
  children: ComponentChildren;
}): VNode {
  useEffect(() => {
    document.title = `Taler Backoffice: ${title}`;
  }, [title]);
  return <Fragment>{children}</Fragment>;
}

export function Menu(_p: MenuProps): VNode {
  const [mobileOpen, setMobileOpen] = useState(false);

  const { i18n } = useTranslationContext();
  const { state, deImpersonate } = useSessionContext();
  const { path } = useNavigationContext();

  const titleWithSubtitle = !state.isAdmin
    ? getInstanceTitle(path, state.instance, i18n)
    : getAdminTitle(path, state.instance, i18n);

  const isLoggedIn = state.status === "loggedIn";

  return (
    <WithTitle title={titleWithSubtitle}>
      <div
        style={{
          backgroundColor: "#00000050",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 10,
          display: mobileOpen ? "flex" : "none",
        }}
        onClick={() => setMobileOpen(false)}
      />
      <div
        class={mobileOpen ? "has-aside-mobile-expanded" : ""}
        onClick={() => setMobileOpen(false)}
      >
        <NavigationBar
          onMobileMenu={() => setMobileOpen(!mobileOpen)}
          mobileOpen={mobileOpen}
          title={titleWithSubtitle}
        />

        {isLoggedIn && <Sidebar mobile={mobileOpen} />}

        {state.status !== "loggedOut" && state.impersonated && (
          <nav
            class="level"
            style={{
              zIndex: 100,
              position: "fixed",
              width: "50%",
              marginLeft: "20%",
            }}
          >
            <div class="level-item has-text-centered has-background-warning">
              <p class="is-size-5">
                You are viewing the instance <b>&quot;{state.instance}&quot;</b>
                .{" "}
                <a
                  href="#/instances"
                  onClick={() => {
                    deImpersonate();
                  }}
                >
                  go back
                </a>
              </p>
            </div>
          </nav>
        )}
      </div>
    </WithTitle>
  );
}

interface NotYetReadyAppMenuProps {
  title: string;
  onShowSettings: () => void;
  onLogout?: () => void;
  isPasswordOk: boolean;
}

const VERSION = typeof __VERSION__ !== "undefined" ? __VERSION__ : undefined;

interface NotConnectedAppMenuProps {
  title: string;
}
export function NotConnectedAppMenu({
  title,
}: NotConnectedAppMenuProps): VNode {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { i18n } = useTranslationContext();
  useEffect(() => {
    document.title = `Taler Backoffice: ${title}`;
  }, [title]);
  const { isTestingEnvironment } = useSettingsContext();

  return (
    <div
      class={mobileOpen ? "has-aside-mobile-expanded" : ""}
      onClick={() => setMobileOpen(false)}
    >
      <NavigationBar
        onMobileMenu={() => setMobileOpen(!mobileOpen)}
        mobileOpen={mobileOpen}
        noBurger
        title={title}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          paddingTop: 8,
          paddingRight: 8,
        }}
      >
        <div>Version {VERSION}</div>
        {!isTestingEnvironment ? undefined : (
          <NotificationCardBulma
            notification={{
              message: i18n.str`Testing environment`,
              description: i18n.str`This server is meant for testing features and configurations. Don't use your personal information here.`,
              type: "INFO",
            }}
          />
        )}
        <LangSelector />
      </div>
    </div>
  );
}

export function NotYetReadyAppMenu({ title }: NotYetReadyAppMenuProps): VNode {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { state } = useSessionContext();

  useEffect(() => {
    document.title = `Taler Backoffice: ${title}`;
  }, [title]);

  const isLoggedIn = state.status === "loggedIn";

  return (
    <div
      class={mobileOpen ? "has-aside-mobile-expanded" : ""}
      onClick={() => setMobileOpen(false)}
    >
      <NavigationBar
        onMobileMenu={() => setMobileOpen(!mobileOpen)}
        mobileOpen={mobileOpen}
        title={title}
      />
      {isLoggedIn && <Sidebar mobile={mobileOpen} />}
    </div>
  );
}
